import React, { useRef, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import MarkdownRenderer from "./MarkdownRenderer";
import { formatPwoName } from "../utils/formatSupSub";

/**
 * JS-driven typing indicator dots.
 *
 * Why JS instead of CSS @keyframes?
 * ----------------------------------
 * React Native Web re-renders cause inline-style reconciliation AND the
 * duplicate <style dangerouslySetInnerHTML> block in SuggestionRail.js was
 * re-mounting @keyframes typing-dot on every parent render, which forced
 * browsers to restart every animation referencing that name.  A setInterval-
 * based approach writes directly to the DOM refs and is immune to both
 * React reconciliation and CSS re-parsing.
 */
const TypingIndicator = React.memo(function TypingIndicator({ isDarkMode, label }) {
  const dot0 = useRef(null);
  const dot1 = useRef(null);
  const dot2 = useRef(null);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const dots = [dot0.current, dot1.current, dot2.current];
    if (dots.some((d) => !d)) return;

    // Animation parameters
    const CYCLE = 1400;         // ms for one full pulse cycle
    const STAGGER = 300;        // ms offset between each dot
    let raf;
    let running = true;
    const start = performance.now();

    function animate(now) {
      if (!running) return;
      const elapsed = now - start;
      for (let i = 0; i < 3; i++) {
        const el = dots[i];
        if (!el) continue;
        // Phase within this dot's offset cycle  (0..1)
        const phase = ((elapsed + CYCLE - i * STAGGER) % CYCLE) / CYCLE;
        // Pulse curve: peak at phase ~0.22, rest at 0 and 0.44+
        let opacity, scale;
        if (phase < 0.22) {
          // Ramp up  (0 -> 0.22)
          const t = phase / 0.22;
          opacity = 0.3 + 0.7 * t;
          scale = 1 + 0.5 * t;
        } else if (phase < 0.44) {
          // Ramp down  (0.22 -> 0.44)
          const t = (phase - 0.22) / 0.22;
          opacity = 1 - 0.7 * t;
          scale = 1.5 - 0.5 * t;
        } else {
          // Rest
          opacity = 0.3;
          scale = 1;
        }
        el.style.opacity = opacity;
        el.style.transform = "scale(" + scale + ")";
      }
      raf = requestAnimationFrame(animate);
    }

    raf = requestAnimationFrame(animate);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
    };
  }, []);

  const dotColor = isDarkMode ? "#60a5fa" : "#2563eb";

  return (
    <View style={{ alignItems: "flex-start" }}>
      <Text
        style={{
          fontSize: 12,
          color: isDarkMode ? "#64748b" : "#94a3b8",
          marginBottom: 4,
          marginLeft: 4,
        }}
      >
        Saras AI
      </Text>
      <View
        style={{
          backgroundColor: isDarkMode ? "#1e293b" : "#f8fafc",
          borderRadius: 12,
          borderBottomLeftRadius: 2,
          paddingVertical: 12,
          paddingHorizontal: 16,
          borderWidth: 1,
          borderColor: isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0",
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", flexDirection: "row", gap: 5 }}>
          <div ref={dot0} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: dotColor, opacity: 0.3, willChange: "transform, opacity" }} />
          <div ref={dot1} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: dotColor, opacity: 0.3, willChange: "transform, opacity" }} />
          <div ref={dot2} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: dotColor, opacity: 0.3, willChange: "transform, opacity" }} />
        </div>
        <Text
          style={{
            fontSize: 13,
            color: isDarkMode ? "#64748b" : "#94a3b8",
            fontWeight: "500",
            fontStyle: "italic",
          }}
        >
          {label}
        </Text>
      </View>
    </View>
  );
});

/**
 * ChatPanel — the active chat experience extracted from SuggestionRail.
 * Renders chat bubbles, typing indicator, and collapsed suggestion pills.
 * The chat input footer stays in SuggestionRail (visible across all panel modes).
 */
function ChatPanel({
  t,
  isDarkMode,
  isCompact,
  dynamicStyles,
  // Chat data
  history,
  loading,
  // Suggestion pills
  allSuggestionItems,
  handlePillPress,
  // Reference handlers
  authToken,
  handleVideoClick,
  handlePWOClick,
  handleChapterClick,
  handleCollectionClick,
  // Audio playback props
  isMuted,
  isPlaying,
  audioUrl,
  onToggleMute,
  onPlayAnswer,
  onStopAudio,
}) {
  const chatScrollRef = useRef(null);
  const latestQuestionY = useRef(0);
  const prevHadPending = useRef(false);
  const trackQuestionUntil = useRef(0); // timestamp: keep re-scrolling to question while refs load

  // Typing-dot animation is now JS-driven (requestAnimationFrame inside
  // TypingIndicator) — no CSS @keyframes injection needed.

  const scrollToBottom = (animated = true) => {
    if (chatScrollRef.current) {
      setTimeout(() => {
        chatScrollRef.current?.scrollToEnd?.({ animated });
      }, 80);
    }
  };

  const scrollToQuestion = (animated = true) => {
    if (chatScrollRef.current && latestQuestionY.current > 0) {
      chatScrollRef.current.scrollTo({
        y: latestQuestionY.current,
        animated,
      });
    }
  };

  const reversed = [...(history || [])].reverse();
  // const hasPendingEntry = reversed.some(
  //   (e) => e?.question && e?.answer == null
  // );
  // BDD 04/21/2026 - Thinking... remained on when I clicked quickly one suggsted quetion to next
  const hasPendingEntry = reversed.some((e) => e?._isPending === true);

  // Scroll to bottom when a new question is added
  useEffect(() => {
    scrollToBottom();
  }, [history?.length]);

  // When answer arrives, scroll to the question and keep tracking for 5s (while refs load)
  useEffect(() => {
    if (prevHadPending.current && !hasPendingEntry) {
      trackQuestionUntil.current = Date.now() + 5000;
      // For the first question, flex-end inflates the onLayout Y — just scroll to top
      if (reversed.length <= 1) {
        setTimeout(() => {
          chatScrollRef.current?.scrollTo?.({ y: 0, animated: true });
        }, 100);
      } else {
        setTimeout(() => scrollToQuestion(), 100);
      }
    }
    prevHadPending.current = hasPendingEntry;
  }, [hasPendingEntry]);

  // Re-scroll to question when content size changes (references popping in)
  const handleContentSizeChange = (_w, _h) => {
    if (Date.now() < trackQuestionUntil.current) {
      if (reversed.length <= 1) {
        chatScrollRef.current?.scrollTo?.({ y: 0, animated: false });
      } else {
        scrollToQuestion(false);
      }
    }
  };

  const hasMessages = reversed.length > 0;

  // ===== Empty state: center suggestions when no chat history =====
  if (!hasMessages && !loading && allSuggestionItems.length > 0) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          paddingHorizontal: dynamicStyles.paddingLeft || 16,
        }}
      >
        <Text
          style={{
            fontSize: 14,
            fontWeight: "600",
            color: isDarkMode ? "#475569" : "#94a3b8",
            letterSpacing: 0.5,
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          {t("Suggested Questions")}
        </Text>
        <View style={{ width: "100%", gap: 10, maxWidth: 420 }}>
          {allSuggestionItems.slice(0, 4).map((item, idx) => (
            <TouchableOpacity
              key={idx}
              onPress={() => handlePillPress(item)}
              activeOpacity={0.7}
              style={{
                paddingVertical: 14,
                paddingHorizontal: 18,
                borderRadius: 14,
                backgroundColor: isDarkMode
                  ? "rgba(37,99,235,0.08)"
                  : "#f8fafc",
                borderWidth: 1,
                borderColor: isDarkMode
                  ? "rgba(96,165,250,0.18)"
                  : "#e2e8f0",
              }}
            >
              <Text
                style={{
                  fontSize: isCompact ? 13 : 14,
                  lineHeight: isCompact ? 19 : 21,
                  color: isDarkMode ? "#93c5fd" : "#1e40af",
                  fontWeight: "500",
                }}
              >
                {item.text}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  }

  return (
    <>
      {/* ===== Chat bubbles ===== */}
      <ScrollView
        ref={chatScrollRef}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingRight: dynamicStyles.paddingRight,
          paddingBottom: 20,
          paddingLeft: dynamicStyles.paddingLeft,
          paddingTop: dynamicStyles.paddingTop,
          justifyContent: "flex-end",
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={true}
        onContentSizeChange={handleContentSizeChange}
      >
        {reversed.map((entry, index) => {
          const stableKey =
            entry?.assistant_turn_id ??
            entry?.student_message_id ??
            (entry?._isPending ? `pending-${index}` : `msg-${index}`);
          const isNewest = index === reversed.length - 1;
          return (
            <View
              key={stableKey}
              style={{ marginBottom: 14 }}
              onLayout={
                isNewest
                  ? (e) => {
                      latestQuestionY.current = e.nativeEvent.layout.y;
                    }
                  : undefined
              }
            >
              {/* User question bubble */}
              {entry?.question ? (
                <View style={{ alignItems: "flex-end", marginBottom: 8 }}>
                  <View
                    style={{
                      maxWidth: "85%",
                      backgroundColor: "#2563eb",
                      borderRadius: 12,
                      borderBottomRightRadius: 2,
                      paddingVertical: 10,
                      paddingHorizontal: 14,
                    }}
                  >
                    <div className="rail-bubble-user-markdown"><MarkdownRenderer content={entry.question} /></div>
                  </View>
                </View>
              ) : null}

              {/* AI response bubble */}
              {entry?.answer ? (
                <View style={{ alignItems: "flex-start" }}>
                  <Text
                    style={{
                      fontSize: 12,
                      color: isDarkMode ? "#64748b" : "#94a3b8",
                      marginBottom: 4,
                      marginLeft: 4,
                    }}
                  >
                    Saras AI
                  </Text>
                  <View
                    style={{
                      maxWidth: "92%",
                      backgroundColor: isDarkMode ? "#1e293b" : "#f8fafc",
                      borderRadius: 12,
                      borderBottomLeftRadius: 2,
                      paddingVertical: 10,
                      paddingHorizontal: 14,
                      borderWidth: 1,
                      borderColor: isDarkMode
                        ? "rgba(148,163,184,0.15)"
                        : "#e2e8f0",
                    }}
                  >
                    <div className="rail-bubble-markdown">
                      <MarkdownRenderer content={entry.answer} />
                    </div>


                    {/* Guide Me actions */}
                    {Array.isArray(entry?.guideMeActions) &&
                      entry.guideMeActions.length > 0 && (
                        <View style={{ marginTop: 10 }}>
                          <Text
                            style={{
                              fontSize: 14,
                              fontWeight: "800",
                              color: isDarkMode ? "#cbd5e1" : "#334155",
                            }}
                          >
                            {"\uD83E\uDDED"} Next actions
                          </Text>
                          <View style={{ marginTop: 6, gap: 6 }}>
                            {entry.guideMeActions.map((a, i) => {
                              const kind = String(
                                a?.kind || ""
                              ).toLowerCase();
                              const label =
                                a?.name ||
                                (kind === "video" ? "Video" : "PWO");
                              const topic = a?.topic_name || null;
                              return (
                                <TouchableOpacity
                                  key={`gm-${i}`}
                                  onPress={() => {
                                    if (kind === "video") {
                                      handleVideoClick?.(
                                        a?.video_id ?? a?.id,
                                        a?.tag_id ?? null,
                                        authToken,
                                        null,
                                        a?.source_topic_id ?? null,
                                        false
                                      );
                                    } else if (kind === "pwo") {
                                      handlePWOClick?.(
                                        a?.pwo_id ?? a?.id,
                                        a?.scene_key ||
                                          a?.scene_name ||
                                          "mainmenu",
                                        authToken,
                                        null,
                                        null,
                                        a?.source_topic_id ?? null,
                                        false
                                      );
                                    }
                                  }}
                                  style={{
                                    paddingVertical: 8,
                                    paddingHorizontal: 10,
                                    borderRadius: 10,
                                    borderWidth: 1,
                                    borderColor: isDarkMode
                                      ? "rgba(148,163,184,0.18)"
                                      : "rgba(148,163,184,0.28)",
                                    backgroundColor: isDarkMode
                                      ? "rgba(15,23,42,0.55)"
                                      : "rgba(255,255,255,0.85)",
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: 14,
                                      fontWeight: "700",
                                      color: isDarkMode
                                        ? "#e2e8f0"
                                        : "#0f172a",
                                    }}
                                  >
                                    {kind === "video" ? "\uD83C\uDFA5 " : "\uD83C\uDFAE "}
                                    {label}
                                  </Text>
                                  {topic && (
                                    <Text
                                      style={{
                                        fontSize: 13,
                                        color: isDarkMode
                                          ? "#94a3b8"
                                          : "#475569",
                                        marginTop: 2,
                                      }}
                                    >
                                      Topic: {topic}
                                    </Text>
                                  )}
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        </View>
                      )}

                    {/* Video references */}
                    {Array.isArray(entry?.videos) &&
                      entry.videos.length > 0 && (
                        <View style={{ marginTop: 10 }}>
                          <Text
                            style={{
                              fontSize: 13,
                              fontWeight: "700",
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                              marginBottom: 4,
                            }}
                          >
                            {"\uD83C\uDFAC"} {t("Video References:")}
                          </Text>
                          {entry.videos.map((video, i) => (
                            <TouchableOpacity
                              key={i}
                              onPress={() =>
                                handleVideoClick?.(
                                  video?.videoID,
                                  video?.tagID,
                                  authToken
                                )
                              }
                            >
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: isDarkMode ? "#60a5fa" : "#2563eb",
                                  marginBottom: 2,
                                }}
                              >
                                {"\uD83C\uDF9E"}{" "}
                                {video?.name
                                  ? `${video.name} - ${video.tag_name || ""}`
                                  : video?.tag_name || "Video"}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}

                    {/* PWO / 3D Simulation references */}
                    {Array.isArray(entry?.pwos) && entry.pwos.length > 0 && (
                      <View style={{ marginTop: 10 }}>
                        <Text
                          style={{
                            fontSize: 13,
                            fontWeight: "700",
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            marginBottom: 4,
                          }}
                        >
                          {"\uD83C\uDFAE"} {t("3D Simulations:")}
                        </Text>
                        {entry.pwos.map((pwo, i) => (
                          <TouchableOpacity
                            key={i}
                            onPress={() =>
                              handlePWOClick?.(
                                pwo?.pwoID,
                                pwo?.pwo_scene_name || "mainmenu",
                                authToken
                              )
                            }
                          >
                            <Text
                              style={{
                                fontSize: 14,
                                color: isDarkMode ? "#60a5fa" : "#2563eb",
                                marginBottom: 2,
                              }}
                            >
                              {"\uD83C\uDFAE"}{" "}
                              {formatPwoName(pwo?.pwo_name || "3D Simulation")} -{" "}
                              {formatPwoName(
                                pwo?.pwo_scene_label ||
                                  pwo?.pwo_scene_name ||
                                  "Scene"
                              )}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}

                    {/* Chapter references */}
                    {Array.isArray(entry?.chapters) &&
                      entry.chapters.length > 0 && (
                        <View style={{ marginTop: 10 }}>
                          <Text
                            style={{
                              fontSize: 13,
                              fontWeight: "700",
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                              marginBottom: 4,
                            }}
                          >
                            {"\uD83D\uDCDA"} {t("Chapters:")}
                          </Text>
                          {entry.chapters.map((ch, i) => (
                            <TouchableOpacity
                              key={i}
                              onPress={() => handleChapterClick?.(ch)}
                            >
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: isDarkMode ? "#60a5fa" : "#2563eb",
                                  marginBottom: 2,
                                }}
                              >
                                {"\uD83D\uDCD6"}{" "}
                                {ch?.chapter_name ||
                                  ch?.name ||
                                  `Chapter ${ch?.chapterId || ch?.id}`}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}

                    {/* Collection references */}
                    {Array.isArray(entry?.collections) &&
                      entry.collections.length > 0 &&
                      (() => {
                        const scoped = entry.collections.find(
                          (c) => c && c.scoped
                        );
                        const others = entry.collections.filter(
                          (c) => c && !c.scoped
                        );
                        return (
                          <View style={{ marginTop: 10 }}>
                            {scoped && (
                              <View
                                style={{
                                  marginBottom: others.length ? 6 : 0,
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 13,
                                    fontWeight: "700",
                                    color: isDarkMode
                                      ? "#94a3b8"
                                      : "#64748b",
                                    marginBottom: 4,
                                  }}
                                >
                                  {"\uD83D\uDCCC"} {t("Current module:")}
                                </Text>
                                <TouchableOpacity
                                  onPress={() =>
                                    handleCollectionClick?.(scoped)
                                  }
                                >
                                  <Text
                                    style={{
                                      fontSize: 14,
                                      color: isDarkMode
                                        ? "#60a5fa"
                                        : "#2563eb",
                                    }}
                                  >
                                  {"\uD83E\uDDE9"}{" "}
                                  {scoped?.display_name ||
                                    scoped?.name_localized ||
                                    scoped?.localized_name ||
                                    scoped?.name ||
                                    `Collection ${
                                      scoped?.collection_id || scoped?.id
                                    }`}
                                    </Text>
                                </TouchableOpacity>
                              </View>
                            )}
                            {others.length > 0 && (
                              <View>
                                <Text
                                  style={{
                                    fontSize: 13,
                                    fontWeight: "700",
                                    color: isDarkMode
                                      ? "#94a3b8"
                                      : "#64748b",
                                    marginBottom: 4,
                                  }}
                                >
                                  {"\uD83D\uDDC2\uFE0F"} {t("Collections:")}
                                </Text>
                                {others.map((c, i) => (
                                  <TouchableOpacity
                                    key={`${
                                      c?.collection_id || c?.id || i
                                    }`}
                                    onPress={() =>
                                      handleCollectionClick?.(c)
                                    }
                                  >
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: isDarkMode
                                          ? "#60a5fa"
                                          : "#2563eb",
                                        marginBottom: 2,
                                      }}
                                    >
                                    {"\uD83E\uDDE9"}{" "}
                                    {c?.display_name ||
                                      c?.name_localized ||
                                      c?.localized_name ||
                                      c?.name ||
                                      `Collection ${
                                        c?.collection_id || c?.id
                                      }`}
                                      </Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            )}
                          </View>
                        );
                      })()}
                  </View>
                </View>
              ) : null}

            </View>
          );
        })}
        {/* Typing indicator — rendered outside the map so it's a stable ScrollView child */}
        {hasPendingEntry && (
          <TypingIndicator isDarkMode={isDarkMode} label={t("Thinking…")} />
        )}
      </ScrollView>

      {/* ===== Collapsed suggestion pills ===== */}
      {allSuggestionItems.length > 0 && (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: isDarkMode
              ? "rgba(148,163,184,0.08)"
              : "#f1f5f9",
            paddingTop: 8,
            paddingBottom: 6,
          }}
        >
          <Text
            style={{
              fontSize: 12,
              fontWeight: "600",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: isDarkMode ? "#475569" : "#94a3b8",
              paddingHorizontal: 12,
              marginBottom: 6,
            }}
          >
            {t("Suggested Questions")} {"\u2192"}
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={true}
            contentContainerStyle={{ paddingHorizontal: 10, gap: 8, alignItems: "stretch" }}
            style={{ flexGrow: 0 }}
          >
            {allSuggestionItems.slice(0, 12).map((item, idx) => (
              <TouchableOpacity
                key={idx}
                onPress={() => handlePillPress(item)}
                activeOpacity={0.7}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  borderRadius: 14,
                  backgroundColor: isDarkMode
                    ? "rgba(37,99,235,0.1)"
                    : "#eff6ff",
                  borderWidth: 1,
                  borderColor: isDarkMode
                    ? "rgba(96,165,250,0.2)"
                    : "#dbeafe",
                  maxWidth: 300,
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: isCompact ? 13 : 14,
                    lineHeight: isCompact ? 18 : 20,
                    color: isDarkMode ? "#60a5fa" : "#2563eb",
                    fontWeight: "500",
                  }}
                >
                  {item.text}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

    </>
  );
}

export default ChatPanel;
